import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import SearchSelect from '@/components/common/SearchSelect';
import { STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { useAuth } from '@/lib/auth';
import { Plus } from 'lucide-react';
import { TaskRow } from '@/pages/staff/MyTasksPage';
import { roleLabel } from '@/lib/roleLabels';

// Admin participant Tasks tab — mirrors the staff portal behaviour.
// "New task" is gated on participant_tasks.create; Support Workers are view-only.
export default function ParticipantTasksTab({ participantId }) {
  const { hasPermission } = useAuth();
  const canCreate = typeof hasPermission === 'function' && hasPermission('participant_tasks.create');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', priority: 'MEDIUM', due_date: '', assignee_staff_id: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/participant-tasks', { params: { participant_id: participantId, page_size: 50 } });
      setData(r.data.data); setDenied(false); setError(false);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else setError(true);
      setData({ items: [] });
    } finally { setLoading(false); }
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open) return;
    api.get('/participant-tasks/assignees', { params: { participant_id: participantId } })
      .then((r) => setAssignees(r.data.data || [])).catch(() => setAssignees([]));
  }, [open, participantId]);

  const items = data?.items || [];

  const create = async () => {
    if (!form.title.trim()) { toast.error('A task title is required'); return; }
    setBusy(true);
    try {
      await api.post('/participant-tasks', {
        participant_id: participantId, title: form.title, description: form.description || undefined,
        priority: form.priority, due_date: form.due_date || undefined,
        assignee_staff_id: form.assignee_staff_id || undefined,
      });
      toast.success('Task created');
      setOpen(false);
      setForm({ title: '', description: '', priority: 'MEDIUM', due_date: '', assignee_staff_id: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || 'Unable to create task');
    } finally { setBusy(false); }
  };

  const act = async (t, action) => {
    setBusy(true);
    try {
      await api.post(`/participant-tasks/${t.id}/${action}`);
      toast.success(action === 'complete' ? 'Task completed' : 'Task started');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  if (!loading && !denied && error) {
    return <div className="py-8 text-center text-sm text-muted-foreground" data-testid="participant-tasks-unavailable">Participant tasks are not enabled for your organisation yet.</div>;
  }

  return (
    <div className="space-y-3" data-testid="participant-tasks-tab">
      {canCreate && (
        <Button size="sm" onClick={() => setOpen(true)} data-testid="task-create-btn"><Plus className="h-4 w-4 mr-1.5" />New task</Button>
      )}
      {loading ? <div className="py-8 text-center text-sm text-muted-foreground" aria-busy="true">Loading…</div>
        : denied ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="participant-tasks-denied">You don&apos;t have permission to view tasks for this participant.</div>
          : items.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="participant-tasks-empty">No tasks recorded for this participant.</div>
            : items.map((t) => <TaskRow key={t.id} t={t} onAction={act} busy={busy} />)}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="task-create-dialog">
          <DialogHeader><DialogTitle>New participant task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Task title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-form-title" aria-label="Task title" />
            <Textarea placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-form-description" aria-label="Task description" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-xs text-muted-foreground space-y-1 block">Priority
                <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-form-priority" aria-label="Priority">
                  <option value="LOW">Low</option><option value="MEDIUM">Medium</option><option value="HIGH">High</option>
                </select>
              </label>
              <label className="text-xs text-muted-foreground space-y-1 block">Due date
                <TrinitiDatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testId="task-form-due-date" />
              </label>
            </div>
            <label className="text-xs text-muted-foreground space-y-1 block">Assign to
              <SearchSelect
                options={assignees.map((a) => ({ value: a.id, label: `${a.full_name}${a.role_name ? ` (${roleLabel(a.role_name)})` : ''}` }))}
                value={form.assignee_staff_id}
                onChange={(v) => setForm({ ...form, assignee_staff_id: v })}
                placeholder="Unassigned"
                searchPlaceholder={STAFF_SEARCH_HINT}
                emptyText="No staff match that search"
                clearLabel="Unassigned"
                className="h-9 text-sm"
                testId="task-form-assignee"
              />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="task-form-cancel">Cancel</Button>
            <Button onClick={create} disabled={busy} data-testid="task-form-submit">Create task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
